import React, { useEffect, useState } from 'react';
import * as Bixby from '$/core';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import { updateUser } from '$/store/user/actions';
import './index.scss';
import {
  getIsCurrentUserBixbyComplianceAdminState,
  getIsSelectedUserAdminState,
  getIsSelectedUserBixbyComplianceAdminState,
  getIsSelectedUserCompanyMNPIInfoAdminState,
  getIsSelectedUserClientComplianceAdminState,
  getIsSelectedUserClientState,
} from '$/store/profile/selectors';
import UserProfileForm, { UpdateUserValues } from '$/components/user-profile-form';
import { UserAdminService } from '$/services/UserAdminService';
import { toastError, toastSuccess } from '$/store/toast/actions';
import { getHasBixbyComplianceFeatureFlagState } from '$/store/login/selectors';

interface IUserProfileProps {
  currentUser?: Bixby.Models.User;
  isLoading: boolean;
  industries?: Bixby.Models.IIdentifiable[];
  titles?: Bixby.Models.IIdentifiable[];
  isCurrentUserBixbyComplianceAdmin: boolean;
  isSelectedUserAdmin: boolean;
  isSelectedUserClient: boolean;
  isSelectedUserBixbyComplianceAdmin: boolean;
  isSelectedUserCompanyMNPIInfoAdmin: boolean;
  isSelectedUserClientComplianceAdmin: boolean;
  hasBixbyComplianceFeatureFlag: boolean;
  dispatchUpdateUser: (updateUser: Bixby.Models.UpdateUserRequest) => Promise<void>;
  dispatchToastSuccess: (message: string) => Promise<void>;
  dispatchToastError: (message: string) => Promise<void>;
}

const UserProfile: React.FC<IUserProfileProps> = props => {
  const {
    currentUser,
    industries,
    titles,
    isCurrentUserBixbyComplianceAdmin,
    isSelectedUserAdmin,
    isSelectedUserClient,
    isSelectedUserBixbyComplianceAdmin,
    isSelectedUserCompanyMNPIInfoAdmin,
    isSelectedUserClientComplianceAdmin,
    hasBixbyComplianceFeatureFlag,
    dispatchUpdateUser,
    dispatchToastSuccess,
    dispatchToastError,
  } = props;
  const [initialUserValues, setInitialUserValues] = useState({} as UpdateUserValues);

  useEffect(() => {
    if (currentUser && !Bixby.Utils.isNullOrEmpty(currentUser)) {
      const currentCompanies =
        currentUser && !Bixby.Utils.isNullOrEmpty(currentUser.companies) ? currentUser.companies : [];

      const initValues = {
        id: currentUser.id,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        industry_Id: currentUser.industry_Id === null ? '' : currentUser.industry_Id,
        title_Id: currentUser.title_Id === null ? '' : currentUser.title_Id,
        companies: currentCompanies,
        notifyOnCompanyAddition: currentUser.notifyOnCompanyAddition,
      } as UpdateUserValues;

      if (isSelectedUserAdmin) {
        initValues.isCompanyMNPIInfoAdmin = isSelectedUserCompanyMNPIInfoAdmin;
      } else {
        initValues.isClientComplianceAdmin = isSelectedUserClientComplianceAdmin;
      }
      setInitialUserValues(initValues);
    }
  }, [currentUser, isSelectedUserAdmin, isSelectedUserCompanyMNPIInfoAdmin, isSelectedUserClientComplianceAdmin]);

  const resendEmail = async () => {
    if (!currentUser) return;
    try {
      await UserAdminService.resendEmailById(currentUser.id);
      dispatchToastSuccess('Email Sent');
    } catch (error) {
      dispatchToastError('Unable to send email');
    }
  };

  const handleUpdateUser = async (values: Bixby.Models.UpdateUserRequest) => {
    const request: Bixby.Models.UpdateUserRequest = {
      id: values.id,
      companies: values.companies,
      firstName: values.firstName,
      industry_Id: values.industry_Id,
      lastName: values.lastName,
      title_Id: values.title_Id,
      notifyOnCompanyAddition: values.notifyOnCompanyAddition,
      isCompanyMNPIInfoAdmin: values.isCompanyMNPIInfoAdmin,
      isClientComplianceAdmin: values.isClientComplianceAdmin,
    };

    dispatchUpdateUser(request);
  };
  return (
    <UserProfileForm
      industries={industries}
      titles={titles}
      currentUser={currentUser}
      initialUserValues={initialUserValues}
      isCurrentUserBixbyComplianceAdmin={isCurrentUserBixbyComplianceAdmin}
      isSelectedUserAdmin={isSelectedUserAdmin}
      isSelectedUserBixbyComplianceAdmin={isSelectedUserBixbyComplianceAdmin}
      updateUser={values => handleUpdateUser(values)}
      resendEmail={resendEmail}
      hasBixbyComplianceFeatureFlag={hasBixbyComplianceFeatureFlag}
      isSelectedUserClient={isSelectedUserClient}
    />
  );
};

const mapStateToProps = (state: AppState) => {
  const isCurrentUserBixbyComplianceAdmin = getIsCurrentUserBixbyComplianceAdminState(state).isBixbyComplianceAdmin;
  const isSelectedUserAdmin = getIsSelectedUserAdminState(state).isAdmin;
  const isSelectedUserClient = getIsSelectedUserClientState(state).isClient;
  const isSelectedUserBixbyComplianceAdmin = getIsSelectedUserBixbyComplianceAdminState(state).isBixbyComplianceAdmin;
  const isSelectedUserCompanyMNPIInfoAdmin = getIsSelectedUserCompanyMNPIInfoAdminState(state).isCompanyMNPIInfoAdmin;
  const isSelectedUserClientComplianceAdmin =
    getIsSelectedUserClientComplianceAdminState(state).isClientComplianceAdmin;
  const hasBixbyComplianceFeatureFlag = getHasBixbyComplianceFeatureFlagState(state).hasBixbyComplianceFeatureFlag;

  return {
    currentUser: state.userState.currentUser,
    isLoading: state.userState.isLoading,
    industries: state.industryState.data,
    titles: state.userTitleState.data,
    isCurrentUserBixbyComplianceAdmin,
    isSelectedUserAdmin,
    isSelectedUserClient,
    isSelectedUserBixbyComplianceAdmin,
    isSelectedUserCompanyMNPIInfoAdmin,
    isSelectedUserClientComplianceAdmin,
    hasBixbyComplianceFeatureFlag,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchUpdateUser: (user: Bixby.Models.UpdateUserRequest) => dispatch(updateUser(user)),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserProfile);
